import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddBookmark } from "./AddBookmark.tsx";
import {
  BookmarkCard,
  getStoredViewMode,
  GridIcon,
  ListIcon,
  shareBookmark,
  storeViewMode,
  type ViewMode,
} from "./BookmarkCard.tsx";
import { BookmarkDetail } from "./BookmarkDetail.tsx";
import { BulkActionToolbar } from "./BulkActionToolbar.tsx";
import { EditBookmark } from "./EditBookmark.tsx";
import { OrphanedTagsDialog } from "./OrphanedTagsDialog.tsx";
import { SwipeableRow } from "./SwipeableRow.tsx";
import { useApp } from "../context/AppContext.tsx";
import { apiDelete, apiPatch } from "../utils/api.ts";
import { toast } from "sonner";
import type { DateFormatOption } from "../../shared/date-format.ts";
import { Tag } from "./Tag.tsx";
import { Button } from "./Button.tsx";
import type { EnrichedBookmark, EnrichedTag } from "../../shared/types.ts";
import { parseSearchQuery } from "../../shared/search-query.ts";

function useIncrementalRender(total: number, pageSize = 100) {
  const [visible, setVisible] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sentinelRef.current || visible >= total) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible((v) => Math.min(v + pageSize, total));
      }
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [visible, total, pageSize]);

  // Snap `visible` back to pageSize only when `total` shrinks (e.g., a
  // filter is applied or bookmarks were deleted). Resetting on every
  // change of `total` would cause the list to remount cards every time
  // /api/initial-data streams in another batched flush — each remount
  // grows the rendered list by `pageSize - 50` rows and torches CLS.
  useEffect(() => {
    setVisible((v) => (v > total ? Math.min(pageSize, total) : v));
  }, [total, pageSize]);

  return { visible, sentinelRef };
}

/** Find tags from deleted bookmarks that no remaining bookmark uses. */
function findOrphanedTags(
  deletedBookmarks: EnrichedBookmark[],
  remainingBookmarks: EnrichedBookmark[],
  allTags: EnrichedTag[],
): EnrichedTag[] {
  const candidates = new Set<string>();
  for (const b of deletedBookmarks) {
    b.tags?.forEach((t) => candidates.add(t));
  }
  if (candidates.size === 0) return [];

  for (const b of remainingBookmarks) {
    b.tags?.forEach((t) => candidates.delete(t));
  }
  if (candidates.size === 0) return [];

  return allTags.filter((t) => candidates.has(t.value));
}

export function BookmarkList() {
  const {
    bookmarks: allBookmarks,
    filteredBookmarks: bookmarks,
    totalBookmarks,
    tags: availableTags,
    selectedTags,
    preferences,
    addBookmark,
    updateBookmark,
    deleteBookmark,
    deleteTag,
    toggleTag,
    refreshData,
    isSyncing,
    loadTags,
    bookmarkSearchQuery,
    setBookmarkSearchQuery,
  } = useApp();
  const dateFormat = preferences.dateFormat as DateFormatOption;
  const { visible, sentinelRef } = useIncrementalRender(bookmarks.length);

  // Data is pre-loaded by App.tsx via loadInitialData(), so no initial loading state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<
    EnrichedBookmark | null
  >(null);
  const [detailBookmark, setDetailBookmark] = useState<EnrichedBookmark | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [dragOverBookmark, setDragOverBookmark] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode>(getStoredViewMode);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Debounced search input
  const [localSearchQuery, setLocalSearchQuery] = useState(bookmarkSearchQuery);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearchQuery(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(
        () => setBookmarkSearchQuery(value),
        150,
      );
    },
    [setBookmarkSearchQuery],
  );
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  // Sync external query changes (e.g. sidebar tag clicks) to local input,
  // and cancel any pending debounce to prevent stale timer overwrites
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    setLocalSearchQuery(bookmarkSearchQuery);
  }, [bookmarkSearchQuery]);

  // Auto-expand mobile search when query becomes non-empty (e.g. sidebar tag click)
  useEffect(() => {
    if (bookmarkSearchQuery.trim()) {
      setMobileSearchOpen(true);
    }
  }, [bookmarkSearchQuery]);

  // Parse query for matching tag suggestions
  const parsedQuery = useMemo(
    () =>
      parseSearchQuery(
        bookmarkSearchQuery,
        availableTags.map((t) => t.value),
      ),
    [bookmarkSearchQuery, availableTags],
  );

  // Matching tags: suggest tags that match the free text portion of the query
  const matchingTags = useMemo(() => {
    const text = parsedQuery.text.toLowerCase();
    if (!text) return [];
    return availableTags
      .filter(
        (t) =>
          t.value.toLowerCase().includes(text) &&
          !selectedTags.has(t.value.toLowerCase()),
      )
      .slice(0, 8);
  }, [parsedQuery.text, availableTags, selectedTags]);

  // Orphaned tags prompt
  const [orphanedTags, setOrphanedTags] = useState<EnrichedTag[]>([]);

  // Select mode state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedUris, setSelectedUris] = useState<Set<string>>(new Set());

  const toggleSelection = useCallback((uri: string) => {
    setSelectedUris((prev) => {
      const next = new Set(prev);
      if (next.has(uri)) next.delete(uri);
      else next.add(uri);
      return next;
    });
  }, []);

  function selectAll() {
    setSelectedUris(new Set(bookmarks.map((b) => b.uri)));
  }

  function deselectAll() {
    setSelectedUris(new Set());
  }

  function exitSelectMode() {
    setIsSelectMode(false);
    setSelectedUris(new Set());
  }

  // Escape key exits select mode
  useEffect(() => {
    if (!isSelectMode) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") exitSelectMode();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSelectMode]);

  function setViewMode(mode: ViewMode) {
    setViewModeState(mode);
    storeViewMode(mode);
  }

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);

  // Pull-to-refresh touch handlers
  useEffect(() => {
    function isAtTop(): boolean {
      // Check both window scroll and main element scroll for PWA compatibility
      const mainElement = document.querySelector("main");
      const windowAtTop = globalThis.scrollY === 0;
      const mainAtTop = !mainElement || mainElement.scrollTop === 0;
      return windowAtTop && mainAtTop;
    }

    function handleTouchStart(e: TouchEvent) {
      if (isSelectMode) return;
      // Only start pull if scrolled to top
      if (isAtTop()) {
        touchStartY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    }

    function handleTouchMove(e: TouchEvent) {
      if (!isPulling || isRefreshing) return;

      const touchY = e.touches[0].clientY;
      const distance = touchY - touchStartY.current;

      // Only pull down, and limit distance
      if (distance > 0 && isAtTop()) {
        e.preventDefault();
        setPullDistance(Math.min(distance, 120));
      }
    }

    function handleTouchEnd() {
      if (!isPulling) return;

      setIsPulling(false);

      // Trigger refresh if pulled far enough
      if (pullDistance > 80) {
        setIsRefreshing(true);
        loadBookmarks().finally(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        });
      } else {
        setPullDistance(0);
      }
    }

    // Listen on document level to catch all touch events
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isPulling, pullDistance, isRefreshing, isSelectMode]);

  // Manual refresh (for pull-to-refresh, refresh button, and retry button)
  async function loadBookmarks() {
    setError(null);
    const toastId = toast("Refreshing bookmarks...");
    try {
      await refreshData(toastId);
    } catch (err: any) {
      setError(err.message);
      toast.error("Refresh failed", { id: toastId });
    }
  }

  function handleBookmarkUpdated(bookmark: EnrichedBookmark) {
    updateBookmark(bookmark);
    setEditingBookmark(null);
    // Update detail view if it's showing the same bookmark
    if (detailBookmark?.uri === bookmark.uri) {
      setDetailBookmark(bookmark);
    }
    toast("Bookmark updated");
  }

  function handleBookmarkDeleted(uri: string) {
    const deleted = allBookmarks.find((b) => b.uri === uri);
    const remaining = allBookmarks.filter((b) => b.uri !== uri);
    deleteBookmark(uri);
    setEditingBookmark(null);
    setDetailBookmark(null);

    if (deleted?.tags?.length) {
      const orphaned = findOrphanedTags([deleted], remaining, availableTags);
      if (orphaned.length > 0) setOrphanedTags(orphaned);
    }
    toast("Bookmark deleted");
  }

  function handleBookmarkAdded(bookmark: EnrichedBookmark) {
    addBookmark(bookmark);
    setShowAddModal(false);
    toast("Bookmark saved");
  }

  async function handleDropTag(bookmarkUri: string, tagValue: string) {
    try {
      // Extract rkey from URI
      const rkey = bookmarkUri.split("/").pop();
      if (!rkey) {
        throw new Error("Invalid bookmark URI");
      }

      // Find the bookmark
      const bookmark = bookmarks.find((b) => b.uri === bookmarkUri);
      if (!bookmark) {
        throw new Error("Bookmark not found");
      }

      // Check if tag already exists
      if (bookmark.tags?.includes(tagValue)) {
        return; // Skip if already tagged
      }

      // Add tag to existing tags
      const newTags = [...(bookmark.tags || []), tagValue];

      // Update via API
      const response = await apiPatch(`/api/bookmarks/${rkey}`, {
        tags: newTags,
      });

      if (!response.ok) {
        throw new Error("Failed to update bookmark tags");
      }

      const data = await response.json();

      // Update context state
      updateBookmark(data.bookmark);
    } catch (err: any) {
      console.error("Failed to add tag to bookmark:", err);
      toast.error(`Failed to add tag: ${err.message}`);
    }
  }

  function handleOpenBookmark(bookmark: EnrichedBookmark) {
    globalThis.open(bookmark.subject, "_blank", "noopener,noreferrer");
  }

  function handleImageError(bookmarkUri: string) {
    setImageErrors((prev) => new Set([...prev, bookmarkUri]));
  }

  async function handleSwipeDelete(bookmark: EnrichedBookmark) {
    const rkey = bookmark.uri.split("/").pop();
    if (!rkey) throw new Error("Invalid bookmark URI");

    const response = await apiDelete(`/api/bookmarks/${rkey}`);
    if (!response.ok) throw new Error("Failed to delete bookmark");

    handleBookmarkDeleted(bookmark.uri);
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 mb-4">Error: {error}</p>
        <Button type="button" onClick={loadBookmarks}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Pull-to-refresh indicator */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: `${pullDistance}px`,
          display: pullDistance > 0 ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--coral)",
          color: "white",
          zIndex: 40,
          transition: isPulling ? "none" : "height 0.3s ease-out",
        }}
      >
        {isRefreshing
          ? (
            <div
              className="spinner"
              style={{
                borderColor: "white transparent transparent transparent",
              }}
            >
            </div>
          )
          : (
            <span style={{ fontSize: "24px" }}>
              {pullDistance > 80 ? "↓" : "↑"}
            </span>
          )}
      </div>

      <div className="mb-6">
        {/* Desktop: side-by-side layout */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Your Bookmarks
              <span className="text-sm font-normal text-gray-500 ml-3">
                {bookmarkSearchQuery.trim()
                  ? `${bookmarks.length} of ${totalBookmarks}`
                  : bookmarks.length}{" "}
                {bookmarks.length === 1 ? "bookmark" : "bookmarks"}
              </span>
            </h2>
            <div className="flex items-center gap-2">
              {bookmarks.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={isSelectMode
                    ? exitSelectMode
                    : () => setIsSelectMode(true)}
                >
                  {isSelectMode ? "Cancel" : "Select"}
                </Button>
              )}
              {!isSelectMode && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setShowAddModal(true)}
                >
                  + Add Bookmark
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                className={`p-2 ${
                  viewMode === "cards" ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                aria-label="Card view"
              >
                <GridIcon active={viewMode === "cards"} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`p-2 ${
                  viewMode === "list" ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                aria-label="List view"
              >
                <ListIcon active={viewMode === "list"} />
              </button>
            </div>
            <button
              type="button"
              onClick={loadBookmarks}
              disabled={isRefreshing}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition"
              aria-label="Refresh bookmarks"
              title="Refresh bookmarks"
            >
              <svg
                className={`w-5 h-5 ${
                  isRefreshing || isSyncing ? "animate-spin" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            <input
              type="text"
              placeholder="Search bookmarks..."
              value={localSearchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition"
            />
          </div>
        </div>

        {/* Mobile: stacked layout with expandable search */}
        <div className="md:hidden">
          {mobileSearchOpen
            ? (
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Search bookmarks..."
                  value={localSearchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setMobileSearchOpen(false);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )
            : (
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-gray-800">
                  {isSelectMode ? "Select Bookmarks" : "Your Bookmarks"}
                </h2>
                <div className="flex items-center gap-1">
                  {isSelectMode
                    ? (
                      <button
                        type="button"
                        onClick={exitSelectMode}
                        className="px-3 py-2 text-sm text-gray-700"
                      >
                        Cancel
                      </button>
                    )
                    : (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setViewMode(
                              viewMode === "cards" ? "list" : "cards",
                            )}
                          className="p-2 text-gray-500 hover:text-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                          aria-label="Toggle view"
                        >
                          {viewMode === "cards"
                            ? <ListIcon active={false} />
                            : <GridIcon active={false} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setMobileSearchOpen(true)}
                          className="p-2 text-gray-500 hover:text-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </button>
                        {bookmarks.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setIsSelectMode(true)}
                            className="p-2 text-gray-500 hover:text-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                            aria-label="Select bookmarks"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                              />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowAddModal(true)}
                          className="px-3 py-2 text-sm rounded-md"
                          style={{
                            backgroundColor: "var(--coral)",
                            color: "white",
                          }}
                        >
                          + Add
                        </button>
                      </>
                    )}
                </div>
              </div>
            )}
          <p className="text-sm text-gray-500">
            {bookmarkSearchQuery.trim()
              ? `${bookmarks.length} of ${totalBookmarks}`
              : bookmarks.length}{" "}
            {bookmarks.length === 1 ? "bookmark" : "bookmarks"}
          </p>
        </div>
      </div>

      {/* Active tag filters and matching tag suggestions */}
      {(selectedTags.size > 0 || matchingTags.length > 0) && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          {selectedTags.size > 0 && (
            <>
              {availableTags
                .filter((t) => selectedTags.has(t.value.toLowerCase()))
                .map((tag) => (
                  <Tag
                    key={tag.uri}
                    variant="selected"
                    shape="row"
                    removable
                    onClick={() => toggleTag(tag.value)}
                  >
                    {tag.value}
                  </Tag>
                ))}
            </>
          )}
          {matchingTags.length > 0 && (
            <>
              {selectedTags.size > 0 && (
                <span className="text-gray-300 mx-1">|</span>
              )}
              {matchingTags.map((tag) => (
                <Tag
                  key={tag.uri}
                  variant="outlined"
                  shape="row"
                  onClick={() => toggleTag(tag.value)}
                >
                  {tag.value}
                </Tag>
              ))}
            </>
          )}
        </div>
      )}

      {bookmarks.length === 0
        ? (
          <div className="text-center py-20">
            <div className="mb-6">
              <span className="text-6xl">📚</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No bookmarks yet
            </h3>
            <p className="text-gray-500 mb-6">
              Start collecting your favorite links!
            </p>
            <Button type="button" onClick={() => setShowAddModal(true)}>
              Add Your First Bookmark
            </Button>
          </div>
        )
        : (
          <>
            <div
              className={viewMode === "cards"
                ? "grid gap-4"
                : "flex flex-col gap-2"}
              style={viewMode === "cards"
                ? {
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                }
                : undefined}
            >
              {bookmarks.slice(0, visible).map((bookmark) => {
                const card = (
                  <BookmarkCard
                    key={bookmark.uri}
                    bookmark={bookmark}
                    viewMode={viewMode}
                    isDragOver={dragOverBookmark === bookmark.uri}
                    imageError={imageErrors.has(bookmark.uri)}
                    dateFormat={dateFormat}
                    isSelectMode={isSelectMode}
                    isSelected={selectedUris.has(bookmark.uri)}
                    onClick={() =>
                      isSelectMode
                        ? toggleSelection(bookmark.uri)
                        : setDetailBookmark(bookmark)}
                    onTagClick={toggleTag}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOverBookmark(bookmark.uri);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      if (e.currentTarget === e.target) {
                        setDragOverBookmark(null);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverBookmark(null);
                      const tagValue = e.dataTransfer.getData("text/plain");
                      if (tagValue) handleDropTag(bookmark.uri, tagValue);
                    }}
                    onImageError={() => handleImageError(bookmark.uri)}
                  />
                );
                if (!isSelectMode && viewMode === "list") {
                  return (
                    <SwipeableRow
                      key={bookmark.uri}
                      onDelete={() => handleSwipeDelete(bookmark)}
                    >
                      {card}
                    </SwipeableRow>
                  );
                }
                return card;
              })}
            </div>
            {visible < bookmarks.length && (
              <div ref={sentinelRef} className="h-10" />
            )}
          </>
        )}

      {showAddModal && (
        <AddBookmark
          onClose={() => setShowAddModal(false)}
          onBookmarkAdded={handleBookmarkAdded}
          availableTags={availableTags}
          onTagsChanged={loadTags}
        />
      )}

      {detailBookmark && !editingBookmark && (
        <BookmarkDetail
          bookmark={detailBookmark}
          onClose={() => setDetailBookmark(null)}
          onEdit={() => setEditingBookmark(detailBookmark)}
          onShare={() => shareBookmark(detailBookmark)}
          onOpen={() => handleOpenBookmark(detailBookmark)}
        />
      )}

      {editingBookmark && (
        <EditBookmark
          bookmark={editingBookmark}
          availableTags={availableTags}
          onClose={() => setEditingBookmark(null)}
          onBookmarkUpdated={handleBookmarkUpdated}
          onBookmarkDeleted={handleBookmarkDeleted}
          onTagsChanged={loadTags}
        />
      )}

      {orphanedTags.length > 0 && (
        <OrphanedTagsDialog
          tags={orphanedTags}
          onComplete={(deletedTagUris) => {
            for (const uri of deletedTagUris) {
              const tag = orphanedTags.find((t) => t.uri === uri);
              deleteTag(uri);
              if (tag && selectedTags.has(tag.value.toLowerCase())) {
                toggleTag(tag.value);
              }
            }
            setOrphanedTags([]);
          }}
          onDismiss={() => setOrphanedTags([])}
        />
      )}

      {isSelectMode && selectedUris.size > 0 && (
        <BulkActionToolbar
          selectedCount={selectedUris.size}
          totalCount={bookmarks.length}
          selectedUris={selectedUris}
          bookmarks={bookmarks}
          availableTags={availableTags}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onComplete={(deletedUris, updatedBookmarks) => {
            const deletedSet = new Set(deletedUris);
            const deleted = allBookmarks.filter((b) => deletedSet.has(b.uri));
            const remaining = allBookmarks.filter(
              (b) => !deletedSet.has(b.uri),
            );
            for (const uri of deletedUris) deleteBookmark(uri);
            for (const b of updatedBookmarks) updateBookmark(b);
            loadTags();
            exitSelectMode();

            if (deletedUris.length > 0) {
              const orphaned = findOrphanedTags(
                deleted,
                remaining,
                availableTags,
              );
              if (orphaned.length > 0) setOrphanedTags(orphaned);
            }
          }}
          onPartialFailure={(deletedUris, updatedBookmarks, failedUris) => {
            const deletedSet = new Set(deletedUris);
            const deleted = allBookmarks.filter((b) => deletedSet.has(b.uri));
            const remaining = allBookmarks.filter(
              (b) => !deletedSet.has(b.uri),
            );
            for (const uri of deletedUris) deleteBookmark(uri);
            for (const b of updatedBookmarks) updateBookmark(b);
            loadTags();
            setSelectedUris(new Set(failedUris));

            if (deletedUris.length > 0) {
              const orphaned = findOrphanedTags(
                deleted,
                remaining,
                availableTags,
              );
              if (orphaned.length > 0) setOrphanedTags(orphaned);
            }
          }}
        />
      )}
    </div>
  );
}
